// Utility functions for localStorage management
import { Timestamp } from 'firebase/firestore';
import type { Child, SleepEvent, SleepGoal, UserProfile } from './types';

const STORAGE_KEYS = {
  SELECTED_CHILD_ID: 'selectedChildId',
  ACTIVE_TAB: 'activeTab',
  MESSAGES_SENT_COUNT: 'messagesSentCount',
  HAS_REQUESTED_REVIEW: 'hasRequestedReview', // Deprecated in favor of timestamp for recurring prompts
  LAST_REVIEW_REQUEST_TIME: 'lastReviewRequestTime',
  FREE_TRIAL_MESSAGES_USED: 'freeTrialMessagesUsed',
} as const;

export function getStoredValue<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') {
    return defaultValue;
  }

  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading localStorage key "${key}":`, error);
    return defaultValue;
  }
}

export function setStoredValue<T>(key: string, value: T): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (value === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error);
  }
}

export function removeStoredValue(key: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
  }
}

export function clearUserPreferences(): void {
  Object.values(STORAGE_KEYS).forEach(key => {
    removeStoredValue(key);
  });
  clearUserDataCache();
}

// Specific getters and setters for app state
export function getSelectedChildId(): string {
  return getStoredValue(STORAGE_KEYS.SELECTED_CHILD_ID, '');
}

export function setSelectedChildId(childId: string): void {
  setStoredValue(STORAGE_KEYS.SELECTED_CHILD_ID, childId);
}

export function getActiveTab(): string {
  return getStoredValue(STORAGE_KEYS.ACTIVE_TAB, 'home');
}


export function setActiveTab(tab: string): void {
  setStoredValue(STORAGE_KEYS.ACTIVE_TAB, tab);
}

export function getMessagesSentCount(): number {
  return getStoredValue(STORAGE_KEYS.MESSAGES_SENT_COUNT, 0);
}

export function incrementMessagesSentCount(): number {
  const current = getMessagesSentCount();
  const next = current + 1;
  setStoredValue(STORAGE_KEYS.MESSAGES_SENT_COUNT, next);
  return next;
}


export function getLastReviewRequestTime(): number {
  return getStoredValue(STORAGE_KEYS.LAST_REVIEW_REQUEST_TIME, 0);
}

export function setLastReviewRequestTime(timestamp: number): void {
  setStoredValue(STORAGE_KEYS.LAST_REVIEW_REQUEST_TIME, timestamp);
}

// Free trial message tracking (optimistic UI cache - backend is source of truth)
export const FREE_TRIAL_LIMIT = 4;

export function getFreeTrialMessagesUsed(): number {
  return getStoredValue(STORAGE_KEYS.FREE_TRIAL_MESSAGES_USED, 0);
}

export function getFreeTrialMessagesRemaining(): number {
  return Math.max(0, FREE_TRIAL_LIMIT - getFreeTrialMessagesUsed());
}

export function incrementFreeTrialMessagesUsed(): number {
  const current = getFreeTrialMessagesUsed();
  const next = current + 1;
  setStoredValue(STORAGE_KEYS.FREE_TRIAL_MESSAGES_USED, next);
  return next;
}

export function setFreeTrialExhausted(): void {
  setStoredValue(STORAGE_KEYS.FREE_TRIAL_MESSAGES_USED, FREE_TRIAL_LIMIT);
}

export function resetFreeTrialMessages(): void {
  removeStoredValue(STORAGE_KEYS.FREE_TRIAL_MESSAGES_USED);
}

// ===== OPTIMISTIC DATA CACHE =====

export function deserializeSleepEvent(event: any): SleepEvent {
  if (!event) return event;
  let transformedTime: Timestamp | null = null;
  if (event.time) {
    if (typeof event.time.toDate === 'function') {
      transformedTime = event.time;
    } else if (event.time instanceof Date) {
      transformedTime = Timestamp.fromDate(event.time);
    } else if (typeof event.time === 'string' || typeof event.time === 'number') {
      const d = new Date(event.time);
      if (!isNaN(d.getTime())) {
        transformedTime = Timestamp.fromDate(d);
      }
    } else if (typeof event.time === 'object') {
      const seconds = event.time.seconds ?? event.time._seconds;
      const nanoseconds = event.time.nanoseconds ?? event.time._nanoseconds ?? 0;
      if (typeof seconds === 'number') {
        transformedTime = new Timestamp(seconds, nanoseconds);
      }
    }
  }
  return {
    ...event,
    time: transformedTime as any,
  };
}

export function deserializeSleepEvents(events: any[]): SleepEvent[] {
  if (!Array.isArray(events)) return [];
  return events.map(deserializeSleepEvent);
}

const CACHE_PREFIXES = {
  PROFILE: 'lullabai_profile_',
  VIP: 'lullabai_vip_',
  CHILDREN: 'lullabai_children_',
  EVENTS: 'lullabai_events_',
  GOALS: 'lullabai_goals_',
} as const;

export function getCachedUserProfile(userId: string): UserProfile | null {
  if (!userId) return null;
  return getStoredValue<UserProfile | null>(`${CACHE_PREFIXES.PROFILE}${userId}`, null);
}

export function setCachedUserProfile(userId: string, profile: UserProfile | null): void {
  if (!userId) return;
  setStoredValue(`${CACHE_PREFIXES.PROFILE}${userId}`, profile);
}

export function getCachedVipStatus(userId: string): boolean | null {
  if (!userId) return null;
  return getStoredValue<boolean | null>(`${CACHE_PREFIXES.VIP}${userId}`, null);
}

export function setCachedVipStatus(userId: string, isVip: boolean): void {
  if (!userId) return;
  setStoredValue(`${CACHE_PREFIXES.VIP}${userId}`, isVip);
}

export function getCachedChildren(userId: string): Child[] | null {
  if (!userId) return null;
  return getStoredValue<Child[] | null>(`${CACHE_PREFIXES.CHILDREN}${userId}`, null);
}

export function setCachedChildren(userId: string, children: Child[]): void {
  if (!userId) return;
  setStoredValue(`${CACHE_PREFIXES.CHILDREN}${userId}`, children);
}

export function getCachedSleepEvents(userId: string, childId: string): SleepEvent[] | null {
  if (!userId || !childId) return null;
  const rawEvents = getStoredValue<any[] | null>(`${CACHE_PREFIXES.EVENTS}${userId}_${childId}`, null);
  if (!rawEvents) return null;
  return deserializeSleepEvents(rawEvents);
}

export function setCachedSleepEvents(userId: string, childId: string, events: SleepEvent[]): void {
  if (!userId || !childId) return;
  setStoredValue(`${CACHE_PREFIXES.EVENTS}${userId}_${childId}`, events);
}

export function getCachedGoals(userId: string, childId: string): SleepGoal[] | null {
  if (!userId || !childId) return null;
  return getStoredValue<SleepGoal[] | null>(`${CACHE_PREFIXES.GOALS}${userId}_${childId}`, null);
}

export function setCachedGoals(userId: string, childId: string, goals: SleepGoal[]): void {
  if (!userId || !childId) return;
  setStoredValue(`${CACHE_PREFIXES.GOALS}${userId}_${childId}`, goals);
}

export function clearUserDataCache(): void {
  if (typeof window === 'undefined') return;
  try {
    const prefixes = Object.values(CACHE_PREFIXES);
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && prefixes.some(prefix => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => window.localStorage.removeItem(k));
  } catch (error) {
    console.error('Error clearing user data cache:', error);
  }
}

